import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield } from 'lucide-react';
import { spotlight, column1Items, column2Items } from './menuData';
import { useMobileNavigationItemHandlers } from './useMobileNavigationItemHandlers';

interface MobileHomepageMenuProps {
  onClose: () => void;
  onNavigationClick?: (href: string) => void;
}

export const MobileHomepageMenu: React.FC<MobileHomepageMenuProps> = ({ 
  onClose, 
  onNavigationClick 
}) => {
  const { handleClick } = useMobileNavigationItemHandlers({
    onNavigationClick,
    onClose
  });

  return (
    <div className="pl-4 space-y-3">
      {/* Spotlight Item */}
      <div className="mb-4 p-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-md border border-blue-200">
        <div className="flex items-center mb-2">
          <spotlight.icon className="w-6 h-6 text-blue-600 mr-2" />
          <span className="text-lg font-semibold text-gray-900">{spotlight.name}</span>
        </div>
        <p className="text-sm text-gray-600 mb-3">{spotlight.description}</p>
        <Link
          to={spotlight.href}
          onClick={(e) => handleClick(spotlight.href, e)}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
          style={{ minHeight: 48, minWidth: 48, touchAction: 'manipulation' }}
        >
          {spotlight.cta}
          <ArrowRight className="ml-2 w-4 h-4" />
        </Link>
      </div>

      {/* Price Beat Guarantee */}
      <Link
        to="/price-beat-guarantee"
        onClick={(e) => handleClick('/price-beat-guarantee', e)}
        className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 transition-colors"
         style={{ minHeight: 48, minWidth: 48, touchAction: 'manipulation' }}
      >
        <Shield className="w-5 h-5 text-green-600 flex-shrink-0" />
        <div>
          <h3 className="font-medium text-gray-900 text-sm">Price Beat Guarantee</h3>
          <p className="text-xs text-gray-600">We beat competitors by 25%</p>
        </div>
      </Link>

      {/* Navigation Items */}
      <div className="space-y-2">
        <h3 className="text-base font-semibold text-gray-900 px-2">Quick Navigation</h3>
        
        {/* Column 1 Items */}
        {column1Items.map((item) => (
          <Link
            key={item.href}
            to={item.href}
            onClick={(e) => handleClick(item.href, e)}
            className="flex items-center gap-3 px-2 py-3 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-100 transition-colors"
            style={{ minHeight: 48, minWidth: 48, touchAction: 'manipulation' }}
            role="menuitem"
            tabIndex={0}
          >
            <item.icon className="h-5 w-5 text-gray-500" />
            <span>{item.name}</span>
          </Link>
        ))}

        {/* Column 2 Items */}
        {column2Items.map((item) => (
          <Link
            key={item.href}
            to={item.href}
            onClick={(e) => handleClick(item.href, e)}
            className="flex items-center gap-3 px-2 py-3 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-100 transition-colors"
            style={{ minHeight: 48, minWidth: 48, touchAction: 'manipulation' }}
            role="menuitem"
            tabIndex={0}
          >
            <item.icon className="h-5 w-5 text-gray-500" />
            <span>{item.name}</span>
          </Link>
        ))}
      </div>
    </div>
  );
};